import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LoginContext } from '../../reactcontext/ReactContext';
import Modal from '../../components/Modal';
import { useNavigate } from 'react-router-dom';
import { years } from '../../data/options';

const UpdatePayment = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { loginInfo } = useContext(LoginContext);
  const [paymentRows, setPaymentRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [agentOptions, setAgentOptions] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [nameOptions, setNameOptions] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [passportOptions, setPassportOptions] = useState([]);
  const [selectedPassport, setSelectedPassport] = useState(null);
  const [selectedFilterYear, setSelectedFilterYear] = useState('all');
  const [selectedModalYear, setSelectedModalYear] = useState(new Date().getFullYear().toString());
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [balance, setBalance] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentMode, setPaymentMode] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [otherDetails, setOtherDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [existingPayments, setExistingPayments] = useState([]);

  const paymentModes = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Card', label: 'Card' },
    { value: 'Online', label: 'Online' },
  ];

  useEffect(() => {
    if (!loginInfo.login) return;
    fetchEmployees();
  }, [loginInfo]);

  const filterPaymentRows = (rows, year, nameFilter, passportFilter, agentFilter) => {
    let filtered = rows;

    if (year && year !== 'all') {
      filtered = filtered.filter(row => String(row.year) === String(year));
    }

    if (nameFilter) {
      filtered = filtered.filter(row => {
        const fullName = `${row.Employee?.firstName || ''} ${row.Employee?.lastName || ''}`.trim();
        return fullName === nameFilter.value;
      });
    }

    if (passportFilter) {
      filtered = filtered.filter(row => (row.Employee?.Passports?.[0]?.passportNo || row.passportNumber) === passportFilter.value);
    }

    if (agentFilter) {
      filtered = filtered.filter(row => row.Employee?.Agent?.name === agentFilter.value);
    }

    return filtered;
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${apiUrl}/payment/getAllPaymentsWithBalance`, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      });

      const records = Array.isArray(res.data) ? res.data : [];
      setPaymentRows(records);
      setFilteredRows(filterPaymentRows(records, selectedFilterYear, selectedName, selectedPassport, selectedAgent));

      const employeeRecords = records
        .map(row => row.Employee)
        .filter(Boolean);

      const agents = Array.from(new Set(employeeRecords.map(emp => emp.Agent?.name || 'Unknown')));
      setAgentOptions(agents.map(a => ({ value: a, label: a })));

      const names = Array.from(new Set(employeeRecords.map(emp => `${emp.firstName} ${emp.lastName}`)));
      setNameOptions(names.map(n => ({ value: n, label: n })));

      const passports = Array.from(new Set(employeeRecords.map(emp => emp.Passports?.[0]?.passportNo).filter(Boolean)));
      setPassportOptions(passports.map(p => ({ value: p, label: p })));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setFilteredRows(filterPaymentRows(paymentRows, selectedFilterYear, selectedName, selectedPassport, selectedAgent));
  }, [paymentRows, selectedFilterYear, selectedName, selectedPassport, selectedAgent]);

  const fetchYearlyPaymentData = async (employeeId, year, totalAmount) => {
    try {
      const res = await axios.get(`${apiUrl}/payment/getPaymentsWithBalance/${employeeId}/${year}`, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      });
      if (res.data.error) {
        setTotalPaid(0);
        setBalance(totalAmount);
        setExistingPayments([]);
      } else {
        setTotalPaid(res.data.totalPayments);
        setBalance(res.data.balance);
        setExistingPayments(res.data.payments || []);
      }
    } catch (err) {
      setTotalPaid(0);
      setBalance(totalAmount);
      setExistingPayments([]);
    }
  };

  const handleEmployeeChange = async (row) => {
    setSelectedRow(row);
    if (row && row.Employee) {
      const employee = row.Employee;
      const selected = {
        value: employee.id,
        label: `${employee.firstName} ${employee.lastName} (${employee.Passports?.[0]?.passportNo || 'N/A'})`,
        data: employee
      };
      setSelectedEmployee(selected);
      setEmployeeDetails(employee);
      const total = parseFloat(row.initialAmount || 0);
      setTotalAmount(total);
      setSelectedModalYear(row.year || new Date().getFullYear().toString());
      await fetchYearlyPaymentData(employee.id, row.year || selectedModalYear, total);
    } else {
      setSelectedEmployee(null);
      setEmployeeDetails(null);
      setTotalAmount(0);
      setTotalPaid(0);
      setBalance(0);
      setExistingPayments([]);
    }
  };

  const handleModalYearChange = async (event) => {
    const year = event.target.value;
    setSelectedModalYear(year);
    if (selectedEmployee) {
      // Find the row with matching employee ID and year
      const matchingRow = paymentRows.find(row => 
        row.Employee?.id === selectedEmployee.value && row.year === year
      );
      
      if (matchingRow) {
        const total = parseFloat(matchingRow.initialAmount || 0);
        setTotalAmount(total);
        await fetchYearlyPaymentData(selectedEmployee.value, year, total);
      } else {
        // If no matching record for this year, reset values
        setTotalAmount(0);
        setTotalPaid(0);
        setBalance(0);
        setExistingPayments([]);
      }
    }
  };

  const handlePaymentAmountChange = (e) => {
    const amt = e.target.value;
    setPaymentAmount(amt);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployee || !paymentAmount || !paymentMode) {
      setError('Please fill all required fields');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${apiUrl}/payment/recordPayment`, {
        employeeId: selectedEmployee.value,
        year: selectedModalYear,
        amount: paymentAmount,
        totalAmount: totalAmount,
        dateOfPayment: paymentDate.toISOString().split('T')[0],
        paymentMode,
        referenceNumber,
        otherDetails,
      }, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setShowUpdateForm(false);
        navigate('/payments');
      }, 1500);
      setPaymentAmount('');
      setReferenceNumber('');
      setOtherDetails('');
      handleEmployeeChange(selectedRow);
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating payment');
    }
    setLoading(false);
  };

  return (
    <div className='w-full bg-gray-50 flex h-full flex-col items-center justify-between relative gap-3'>
      <div className='self-start ml-10 mt-10'>
        <Link to={'/payments'} className='text-yellow-700'>Payments</Link> {' > '} Update Payment
      </div>
      <div className='w-full p-4 flex flex-col items-center justify-center gap-4'>
        <h1 className='text-2xl font-bold mb-6 text-center'>Update Payment</h1>
        {/* Filter Section */}
        <div className='w-full flex flex-col items-center'>
          <div className='w-full max-w-full bg-white shadow-lg rounded-xl p-8 flex flex-wrap justify-center gap-8 mb-10 border border-gray-200'>
            <Select
              options={nameOptions}
              value={selectedName}
              onChange={setSelectedName}
              placeholder='Search by name'
              isClearable
              isSearchable
              className='w-72 min-w-[220px]'
              styles={{
                control: (base) => ({ ...base, minHeight: 48, fontSize: 16 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            <Select
              options={passportOptions}
              value={selectedPassport}
              onChange={setSelectedPassport}
              placeholder='Search by passport number'
              isClearable
              isSearchable
              className='w-72 min-w-[220px]'
              styles={{
                control: (base) => ({ ...base, minHeight: 48, fontSize: 16 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            <Select
              options={agentOptions}
              value={selectedAgent}
              onChange={setSelectedAgent}
              placeholder='Filter by agent'
              isClearable
              isSearchable
              className='w-72 min-w-[220px]'
              styles={{
                control: (base) => ({ ...base, minHeight: 48, fontSize: 16 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            <div className='w-72 min-w-[220px]'>
              <select
                value={selectedFilterYear}
                onChange={(e) => setSelectedFilterYear(e.target.value)}
                className='w-full p-3 border rounded focus:ring-2 focus:ring-blue-500'
              >
                <option value='all'>All Years</option>
                {years.map(year => (
                  <option key={year} value={String(year)}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* Employee List */}
        <div className='w-full px-4'>
          <table className='w-full bg-white shadow rounded'>
            <thead>
              <tr className='bg-gray-200'>
                <th className='p-2'>Name</th>
                <th className='p-2'>Passport</th>
                <th className='p-2'>Nationality</th>
                <th className='p-2'>Agent</th>
                <th className='p-2'>Year</th>
                <th className='p-2'>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, index) => (
                <tr key={`${row.Employee?.id || index}-${row.year}`} className='border-t'>
                  <td className='p-2'>{row.Employee?.firstName} {row.Employee?.lastName}</td>
                  <td className='p-2'>{row.Employee?.Passports?.[0]?.passportNo || row.passportNumber || 'N/A'}</td>
                  <td className='p-2'>{row.Employee?.nationality}</td>
                  <td className='p-2'>{row.Employee?.Agent?.name || 'Unknown'}</td>
                  <td className='p-2'>{row.year || 'N/A'}</td>
                  <td className='p-2'>
                    <button
                      className='bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600'
                      onClick={() => {
                        handleEmployeeChange(row);
                        setShowUpdateForm(true);
                      }}
                    >
                      Update
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Update Form Modal */}
        <Modal
          isOpen={showUpdateForm}
          onClose={() => setShowUpdateForm(false)}
          dimensions="w-11/12 max-w-4xl h-auto max-h-[80vh] overflow-y-auto"
        >
          <div className='w-full bg-white rounded-lg shadow-lg'>
            <div className='bg-gradient-to-r from-blue-50 to-blue-100 rounded-t-lg px-8 pt-6 pb-2 border-b-2 border-gray-200 shadow-sm'>
              <h3 className='text-3xl font-extrabold text-blue-800 tracking-wide text-center mb-1'>Update Payment</h3>
              <p className='text-sm text-blue-600 text-center mt-2 font-medium'>Add additional payments for the employee</p>
            </div>
            <div className='w-full bg-white'>
              <div className='px-0 py-4 bg-white rounded-none shadow-none border-b border-blue-100 w-full text-center'>
                <h3 className='text-xl font-bold text-blue-700 mb-3 tracking-wide'>Employee Details</h3>
                <div className='space-y-2 text-base flex flex-col items-center'>
                  <div><span className='font-semibold text-gray-700'>Name:</span> <span className='text-gray-900'>{employeeDetails?.firstName} {employeeDetails?.lastName}</span></div>
                  <div><span className='font-semibold text-gray-700'>Passport:</span> <span className='text-gray-900'>{employeeDetails?.Passports?.[0]?.passportNo || 'N/A'}</span></div>
                  <div><span className='font-semibold text-gray-700'>Nationality:</span> <span className='text-gray-900'>{employeeDetails?.nationality}</span></div>
                  <div><span className='font-semibold text-gray-700'>Agent:</span> <span className='text-gray-900'>{employeeDetails?.Agent?.name}</span></div>
                  <div><span className='font-semibold text-gray-700'>Total Amount:</span> <span className='text-gray-900'>RM {totalAmount.toFixed(2)}</span></div>
                  <div><span className='font-semibold text-gray-700'>Total Paid:</span> <span className='text-gray-900'>RM {totalPaid.toFixed(2)}</span></div>
                  <div><span className='font-semibold text-gray-700'>Balance:</span> <span className='text-gray-900'>RM {balance.toFixed(2)}</span></div>
                </div>
              </div>
            </div>
            {/* Existing Payments */}
            {existingPayments.length > 0 && (
              <div className='px-8 py-4'>
                <h4 className='text-lg font-semibold mb-3'>Existing Payments</h4>
                <table className='w-full border-collapse border border-gray-300'>
                  <thead>
                    <tr className='bg-gray-100'>
                      <th className='border border-gray-300 p-2'>Date</th>
                      <th className='border border-gray-300 p-2'>Amount</th>
                      <th className='border border-gray-300 p-2'>Mode</th>
                      <th className='border border-gray-300 p-2'>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingPayments.map(payment => (
                      <tr key={payment.id}>
                        <td className='border border-gray-300 p-2'>{payment.dateOfPayment}</td>
                        <td className='border border-gray-300 p-2'>RM {parseFloat(payment.amount).toFixed(2)}</td>
                        <td className='border border-gray-300 p-2'>{payment.paymentMode}</td>
                        <td className='border border-gray-300 p-2'>{payment.referenceNumber || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <form onSubmit={handleSubmit} className='p-8'>
              <div className='mb-6'>
                <h4 className='text-lg font-semibold mb-3 border-b pb-1'>Add New Payment</h4>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Year</label>
                    <input
                      type='text'
                      value={selectedModalYear}
                      readOnly
                      className='w-full p-2 border rounded bg-gray-100 text-gray-600 cursor-not-allowed'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Payment Amount</label>
                    <input
                      type='number'
                      step='0.01'
                      value={paymentAmount}
                      onChange={handlePaymentAmountChange}
                      className='w-full p-2 border rounded focus:ring-2 focus:ring-blue-500'
                      required
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Payment Date</label>
                    <DatePicker
                      selected={paymentDate}
                      onChange={setPaymentDate}
                      className='w-full p-2 border rounded focus:ring-2 focus:ring-blue-500'
                      dateFormat='yyyy-MM-dd'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Payment Mode</label>
                    <Select
                      options={paymentModes}
                      value={paymentModes.find(m => m.value === paymentMode)}
                      onChange={(selected) => setPaymentMode(selected.value)}
                      placeholder="Select payment mode"
                      className='w-full'
                      styles={{
                        control: (base) => ({ ...base, padding: '4px', borderRadius: '4px' }),
                      }}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Reference Number (Optional)</label>
                    <input
                      type='text'
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      className='w-full p-2 border rounded focus:ring-2 focus:ring-blue-500'
                    />
                  </div>
                </div>
              </div>
              {error && <p className='text-red-500 mb-4 text-center'>{error}</p>}
              <div className='flex justify-end gap-4 border-t pt-4'>
                <button
                  type='button'
                  onClick={() => setShowUpdateForm(false)}
                  className='px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={loading}
                  className='px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50'
                >
                  {loading ? 'Updating...' : 'Add Payment'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
      {showSuccess && (
        <div className='fixed inset-0 flex items-center justify-center z-50'>
          <div className='bg-white border border-green-300 shadow-lg rounded-xl px-8 py-6 flex flex-col items-center'>
            <svg className='w-12 h-12 text-green-500 mb-2' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
            </svg>
            <div className='text-lg font-semibold text-green-700 mb-1'>Payment updated successfully!</div>
            <div className='text-gray-500'>Redirecting to General Payments...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdatePayment;